import type { Guide, GuideRecordHint } from '../types';

/**
 * S-26.10-06 — lista „Do zrobienia” w oknie kontroli: decyzje o spółce i stawce VAT, eksport do TMS
 * zablokowany do decyzji (scenariusz e2e 36; makieta p. Mariusza z 21.10, BR-ADM-14, ścieżka ADM-09).
 * Rekordy z `scenario-state.mjs`: D36A — zlecenie bezpośrednie Administracji bez decyzji (nagrywarka
 * niczego nie zapisuje), D36B — decyzje podjęte, potem zmiana kontrahenta, więc decyzja o VAT jest
 * nieaktualna.
 */
const CONTROL: GuideRecordHint = {
  description: 'prezlecenie u Administracji, zaakceptowane, przed eksportem — z kolejki „Do kontroli”',
  listPath: '/pre-orders',
  listFilter: { intakeQueue: 'adm_control' },
};

export const guide: Guide = {
  flagKey: 'intakeControlDecisions',
  title: 'Okno kontroli: lista „Do zrobienia”, decyzje o spółce i stawce VAT',
  whatItDoes:
    'W oknie kontroli prezlecenia jest lista „Do zrobienia (n)” — decyzje, które Administracja podejmuje przed eksportem do TMS. Spółkę i stawkę VAT potwierdza się albo wybiera inną z uzasadnieniem wyjątku. Podpowiedzi spółki z reguły klienta nie ma — widać spółkę z prezlecenia i skąd jest. Dopóki lista nie jest pusta, „Eksportuj do TMS” jest nieaktywne. Gdy dane, na których oparto decyzję, się zmienią, decyzja wraca na listę. Każda decyzja: kto, kiedy, co — w historii.',
  audience: ['Administracja', 'administrator'],
  presentationItems: ['S-26.10-06'],
  steps: [
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'intake-decisions',
      title: 'Lista „Do zrobienia (n)”',
      description:
        'Nad stanem zapisu stoi lista decyzji przed eksportem. Liczba w nagłówku mówi, ile czeka. Pozycja „czeka na decyzję” zostaje na liście, dopóki ktoś z Administracji jej nie rozstrzygnie.',
      action: 'Otwórz okno kontroli prezlecenia z kolejki „Do kontroli” i przejrzyj listę.',
      recorder: { account: 'adm', demo: { kind: 'scenario', key: 'd36aPreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'intake-decision-company',
      mockupTestId: 'adm-zlecenie-decyzja-spolka',
      title: 'Spółka: „Potwierdź” albo „Inna…”',
      description:
        'Widać spółkę z prezlecenia i skąd jest (kto ją wpisał, z kopii albo z wiadomości). Podpowiedzi z reguły klienta nie ma, dopóki Marathon jej nie poda. „Potwierdź” zapisuje decyzję; „Inna…” zmienia spółkę zlecenia z uzasadnieniem.',
      action: 'Sprawdź spółkę i kliknij „Potwierdź” albo „Inna…”.',
      recorder: { account: 'adm', demo: { kind: 'scenario', key: 'd36aPreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'intake-decision-reason',
      title: 'Wyjątek z uzasadnieniem',
      description:
        'Inna spółka niż na prezleceniu to wyjątek dla tego zlecenia: wybierz spółkę i wpisz uzasadnienie (co najmniej 10 znaków). Reguła klienta się nie zmienia, a zatwierdzony zapis wraca do szkicu.',
      action: 'Wybierz spółkę, wpisz uzasadnienie, np. „dyspozycja handlowca”, i kliknij „Zapisz decyzję”.',
      recorder: {
        account: 'adm',
        demo: { kind: 'scenario', key: 'd36aPreOrderId' },
        actions: [{ kind: 'click', target: 'intake-decision-company-other' }],
      },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'intake-decision-vat',
      mockupTestId: 'adm-zlecenie-decyzja-vat',
      title: 'Stawka VAT: podpowiedź do potwierdzenia',
      description:
        'Podpowiedź stawki z krajów spółki, kontrahenta, załadunku i rozładunku, z uzasadnieniem. „Potwierdź” albo „Inna…” z uzasadnieniem — decyzja zostaje w historii. Stawkę i tak wpisuje się w TMS.',
      action: 'Porównaj podpowiedź z tym, co wpiszesz w TMS, i zdecyduj.',
      recorder: { account: 'adm', demo: { kind: 'scenario', key: 'd36aPreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'adm-zlecenie-eksportuj-tms',
      mockupTestId: 'adm-zlecenie-eksportuj-tms',
      title: 'Eksport czeka na decyzje',
      description:
        '„Eksportuj do TMS (n)” jest nieaktywne, dopóki lista nie jest pusta, a pod przyciskami stoi, czego brakuje. Serwer też odmawia zatwierdzenia i pliku.',
      action: 'Podejmij wszystkie decyzje — przycisk eksportu stanie się aktywny.',
      recorder: { account: 'adm', demo: { kind: 'scenario', key: 'd36aPreOrderId' } },
    },
    {
      route: '/pre-orders/:preOrderId/control',
      record: CONTROL,
      target: 'intake-decisions-history',
      title: 'Decyzja nieaktualna i historia decyzji',
      description:
        'Gdy po decyzji zmienią się dane, na których ją oparto (np. inny kontrahent zmienia podpowiedź VAT), decyzja jest „nieaktualna” i wraca na listę. Pod listą — historia: kto, kiedy, co postanowił, także decyzje zastąpione.',
      action: 'Rozwiń „Historia decyzji” i zdecyduj jeszcze raz o pozycji nieaktualnej.',
      recorder: {
        account: 'adm',
        demo: { kind: 'scenario', key: 'd36bPreOrderId' },
        actions: [{ kind: 'click', target: 'intake-decisions-history-toggle' }],
      },
    },
  ],
};
